import { Controller } from "@hotwired/stimulus"

// Native in-app send confirmation (owner directive 2026-08-13: no browser
// confirm() anywhere; turbo_confirm is dead on turbo:false forms). Wraps a
// form: submit opens a styled overlay whose buttons decide the hidden
// `reset` field, then submits natively.
//
// Values:
//   message      - the modal body text
//   resetLabel   - primary button; sets reset=1 (omit for a plain confirm)
//   noResetLabel - secondary button; sets reset=0
//   noResetHint  - small explanation under the no-reset button
//   confirmLabel - plain-confirm primary label (when no reset choice)
//   recommendNoReset - flips the door order/styling so DON'T reset leads
//                      (client emails; the team report leads with reset)
export default class extends Controller {
  static targets = ["editableBody"]
  static values = {
    message: String,
    resetLabel: String,
    resetHint: String,
    noResetLabel: String,
    noResetHint: String,
    confirmLabel: String,
    recommendNoReset: Boolean
  }

  connect() {
    // bfcache back-restore resurrects the page with the latch still set
    // (Seat 3 C2): a later Send would bypass the modal. pageshow with
    // persisted=true is exactly that restore.
    this.boundPageshow = (e) => { if (e.persisted) this.confirmed = false }
    window.addEventListener("pageshow", this.boundPageshow)
  }

  disconnect() {
    window.removeEventListener("pageshow", this.boundPageshow)
  }

  submit(event) {
    if (this.confirmed) return
    event.preventDefault()
    if (this.modalOpen) return
    this.openModal()
  }

  openModal() {
    const backdrop = document.createElement("div")
    backdrop.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:9999;"

    const buttons = []
    if (this.hasResetLabelValue && this.resetLabelValue) {
      const lead = this.recommendNoResetValue ? "no-reset" : "reset"
      const resetBtn = [`<button type="button" data-role="reset" class="${lead === "reset" ? "spt-btn-primary" : "spt-btn-secondary"}" style="width:100%;">${this.esc(this.resetLabelValue)}</button>`]
      if (this.hasResetHintValue && this.resetHintValue) {
        resetBtn.push(`<p style="font-size:11px;color:#8C8C8C;margin:2px 0 6px;text-align:center;">${this.esc(this.resetHintValue)}</p>`)
      }
      const noResetBtn = [`<button type="button" data-role="no-reset" class="${lead === "no-reset" ? "spt-btn-primary" : "spt-btn-secondary"}" style="width:100%;">${this.esc(this.noResetLabelValue || "Send without resetting")}</button>`]
      if (this.hasNoResetHintValue && this.noResetHintValue) {
        noResetBtn.push(`<p style="font-size:11px;color:#8C8C8C;margin:2px 0 6px;text-align:center;">${this.esc(this.noResetHintValue)}</p>`)
      }
      const ordered = lead === "no-reset" ? [...noResetBtn, ...resetBtn] : [...resetBtn, ...noResetBtn]
      buttons.push(...ordered)
    } else {
      buttons.push(`<button type="button" data-role="confirm" class="spt-btn-primary" style="width:100%;">${this.esc(this.confirmLabelValue || "Confirm")}</button>`)
    }
    buttons.push(`<button type="button" data-role="cancel" class="spt-btn-ghost" style="width:100%;">Cancel</button>`)

    const modal = document.createElement("div")
    modal.setAttribute("role", "dialog")
    modal.setAttribute("aria-modal", "true")
    modal.style.cssText = "background:var(--spt-bg-white,#fff);border:1px solid var(--spt-border,#E8E4DF);border-radius:12px;box-shadow:0 12px 40px rgba(0,0,0,0.2);max-width:380px;width:calc(100% - 32px);padding:20px;display:flex;flex-direction:column;gap:8px;"
    // The client form echoes the live To value into the message (reads
    // better than "the address in the To field").
    let message = this.messageValue
    const toField = this.element.querySelector("[name=to_email]")
    if (toField && toField.value) message = message.replace("the address in the To field", toField.value)
    modal.innerHTML = `<p style="font-size:14px;color:#2D2D2D;margin:0 0 8px;line-height:1.5;">${this.esc(message)}</p>${buttons.join("")}`
    backdrop.appendChild(modal)
    document.body.appendChild(backdrop)
    this.modalOpen = true
    // Focus moves INTO the modal (Seat 2 C1): Enter behind the overlay was
    // re-firing submit and stacking duplicate modals.
    modal.querySelector("button")?.focus()

    const close = () => { backdrop.remove(); this.modalOpen = false; document.removeEventListener("keydown", onKey) }
    const onKey = (e) => { if (e.key === "Escape") close() }
    document.addEventListener("keydown", onKey)
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close() })

    const go = (resetValue) => {
      const field = this.element.querySelector("[name=reset]")
      if (field && resetValue !== null) field.value = resetValue
      // The edited email rides a hidden field (owner notes 2026-08-13: the
      // compose body is editable in place).
      if (this.hasEditableBodyTarget) {
        const bodyField = this.element.querySelector("[name=body_html]")
        if (bodyField) bodyField.value = this.editableBodyTarget.innerHTML
      }
      close()
      this.confirmed = true
      this.element.requestSubmit ? this.element.requestSubmit() : this.element.submit()
    }
    modal.querySelector("[data-role=reset]")?.addEventListener("click", () => go("1"))
    modal.querySelector("[data-role=no-reset]")?.addEventListener("click", () => go("0"))
    modal.querySelector("[data-role=confirm]")?.addEventListener("click", () => go(null))
    modal.querySelector("[data-role=cancel]").addEventListener("click", close)
  }

  esc(text) {
    const d = document.createElement("div")
    d.textContent = text || ""
    return d.innerHTML
  }
}
